import type {
  WorkOrder,
  WorkOrderType,
  WorkOrderStatus,
  WorkOrderPriority,
  Technician,
  WorkOrderStats,
  WorkOrderAction,
} from "../types";

const workOrderTypes: WorkOrderType[] = [
  "earth_moving", "road_works", "building_works", "machinery_parked",
  "agi_activity", "marker_posts", "forestry", "quarry", "flooding",
  "ditching", "network_works", "fencing", "other", "unidentified"
];

const statuses: WorkOrderStatus[] = ["open", "dispatched", "tech_aware", "on_site", "completed", "closed"];
const priorities: WorkOrderPriority[] = ["critical", "high", "medium", "low"];
const zones = ["SN1", "SN2", "SN3", "SN4", "SN5", "W1", "W2", "W3", "W4", "W5", "E1", "E2", "E3", "E4", "E5"];

const locations = [
  "A45 Junction near Coventry",
  "B4114 Bedworth Road",
  "M6 Corridor Section 12",
  "Hinckley Road Industrial Estate",
  "Nuneaton Bypass",
  "Rugby Eastern Relief Road",
  "Lutterworth Feeder Station Area",
  "Market Harborough Pipeline Crossing",
  "Daventry AGI Compound",
  "Northampton Western Bypass",
  "Kettering Industrial Zone",
  "Corby Steel Works Perimeter",
  "Wellingborough River Crossing",
  "Rushden Lakes Development Area",
  "A14 Cambridge Junction",
];

export const technicians: Technician[] = [
  { id: "tech-001", name: "James Wilson", phone: "+44 7700 900001", email: "j.wilson@ngt.com", zone: "SN3", status: "available" },
  { id: "tech-002", name: "Sarah Mitchell", phone: "+44 7700 900002", email: "s.mitchell@ngt.com", zone: "SN4", status: "busy", currentWorkOrderId: "WO-2024001" },
  { id: "tech-003", name: "David Brown", phone: "+44 7700 900003", email: "d.brown@ngt.com", zone: "W2", status: "available" },
  { id: "tech-004", name: "Emma Thompson", phone: "+44 7700 900004", email: "e.thompson@ngt.com", zone: "W3", status: "busy" },
  { id: "tech-005", name: "Michael Davis", phone: "+44 7700 900005", email: "m.davis@ngt.com", zone: "E1", status: "offline" },
  { id: "tech-006", name: "Lisa Anderson", phone: "+44 7700 900006", email: "l.anderson@ngt.com", zone: "E2", status: "available" },
  { id: "tech-007", name: "Robert Taylor", phone: "+44 7700 900007", email: "r.taylor@ngt.com", zone: "SN1", status: "available" },
  { id: "tech-008", name: "Jennifer White", phone: "+44 7700 900008", email: "j.white@ngt.com", zone: "W5", status: "busy" },
];

const generateMapReference = (): string => {
  const letters = ["SP", "SK", "TL", "TF", "SE", "NZ"];
  const letter = letters[Math.floor(Math.random() * letters.length)];
  const easting = Math.floor(Math.random() * 90000 + 10000);
  const northing = Math.floor(Math.random() * 90000 + 10000);
  return `${letter} ${easting} ${northing}`;
};

const generateRefNumber = (zone: string, index: number): string => {
  const year = "26";
  const week = String(Math.floor(Math.random() * 52) + 1).padStart(2, "0");
  const seq = String(index).padStart(4, "0");
  return `${year}${week}W${zone} ${seq}`;
};

const generateCoordinates = (): [number, number] => {
  const lng = -1.5 + (Math.random() * 1);
  const lat = 52.0 + (Math.random() * 0.8);
  return [lng, lat];
};

const getTypeDescription = (type: WorkOrderType): string => {
  const descriptions: Record<WorkOrderType, string[]> = {
    earth_moving: ["Investigate excavation activity near pipeline", "Verify ground disturbance report", "Assess trenching operation safety"],
    road_works: ["Inspect road works near pipeline crossing", "Verify utility works permits", "Monitor surface repairs"],
    building_works: ["Assess construction site proximity to assets", "Verify building foundation depths", "Inspect development boundary"],
    machinery_parked: ["Investigate parked machinery near marker", "Verify equipment ownership and purpose", "Assess potential risk"],
    agi_activity: ["Inspect unscheduled AGI activity", "Verify contractor access authorization", "Check AGI perimeter security"],
    marker_posts: ["Replace damaged marker post", "Clear vegetation around marker", "Repair leaning marker"],
    forestry: ["Inspect tree felling operation", "Verify forestry permits", "Assess root removal risk"],
    quarry: ["Monitor quarry boundary expansion", "Inspect extraction proximity", "Verify quarry permits"],
    flooding: ["Assess flood damage to assets", "Inspect waterlogged areas", "Check drainage systems"],
    ditching: ["Verify ditch excavation depth", "Inspect drainage works", "Monitor agricultural ditching"],
    network_works: ["Conduct scheduled inspection", "Perform maintenance check", "Verify CP readings"],
    fencing: ["Inspect new fencing installation", "Verify fence post depths", "Check security fencing"],
    other: ["Investigate reported activity", "General site inspection", "Verify third-party report"],
    unidentified: ["Investigate unknown activity", "Field verification required", "Assess and categorize activity"],
  };
  const options = descriptions[type];
  return options[Math.floor(Math.random() * options.length)];
};

const getSLAHours = (priority: WorkOrderPriority): number => {
  switch (priority) {
    case "critical": return 1;
    case "high": return 4;
    case "medium": return 24;
    case "low": return 72;
  }
};

const generateWorkOrder = (index: number): WorkOrder => {
  const zone = zones[Math.floor(Math.random() * zones.length)];
  const status = statuses[Math.floor(Math.random() * statuses.length)];
  const type = workOrderTypes[Math.floor(Math.random() * workOrderTypes.length)];
  const priority = priorities[Math.floor(Math.random() * priorities.length)];

  const now = new Date();
  const daysAgo = Math.floor(Math.random() * 14);
  const hoursAgo = Math.floor(Math.random() * 24);
  const createdDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000 - hoursAgo * 60 * 60 * 1000);

  const slaHours = getSLAHours(priority);
  const slaDeadline = new Date(createdDate.getTime() + slaHours * 60 * 60 * 1000);
  const slaBreached = status !== "closed" && status !== "completed" && now > slaDeadline;

  // Assign technician for non-open statuses
  const assignedTech = status !== "open"
    ? technicians[Math.floor(Math.random() * technicians.length)]
    : undefined;

  const workOrder: WorkOrder = {
    id: `WO-${String(2024001 + index).padStart(7, "0")}`,
    referenceNumber: generateRefNumber(zone, index),
    alertId: Math.random() > 0.5 ? `ALT-${String(1000 + index).padStart(5, "0")}` : undefined,
    sightingId: Math.random() > 0.3 ? `WO-${String(2024000 + Math.floor(Math.random() * 60)).padStart(7, "0")}` : undefined,
    type,
    status,
    priority,
    location: locations[Math.floor(Math.random() * locations.length)],
    mapReference: generateMapReference(),
    coordinates: generateCoordinates(),
    zone,
    description: getTypeDescription(type),
    assignedTechnician: assignedTech,
    createdAt: createdDate.toISOString(),
    dispatchedAt: ["dispatched", "tech_aware", "on_site", "completed", "closed"].includes(status)
      ? new Date(createdDate.getTime() + 15 * 60 * 1000).toISOString() : undefined,
    techAwareAt: ["tech_aware", "on_site", "completed", "closed"].includes(status)
      ? new Date(createdDate.getTime() + 30 * 60 * 1000).toISOString() : undefined,
    onSiteAt: ["on_site", "completed", "closed"].includes(status)
      ? new Date(createdDate.getTime() + 90 * 60 * 1000).toISOString() : undefined,
    completedAt: ["completed", "closed"].includes(status)
      ? new Date(createdDate.getTime() + 180 * 60 * 1000).toISOString() : undefined,
    closedAt: status === "closed"
      ? new Date(createdDate.getTime() + 240 * 60 * 1000).toISOString() : undefined,
    slaDeadline: slaDeadline.toISOString(),
    slaBreached,
    resolution: status === "closed" || status === "completed" ? "Activity verified. No risk to pipeline assets. Work authorized with valid permit." : undefined,
    notes: generateNotes(status),
    actions: generateActions(status, createdDate, assignedTech?.name),
  };

  return workOrder;
};

const generateNotes = (status: WorkOrderStatus) => {
  if (status === "open") return [];

  const notes = [
    {
      id: "note-1",
      author: "Control Room",
      content: "Work order created from detection. Awaiting technician dispatch.",
      timestamp: new Date().toISOString(),
    },
  ];

  if (["on_site", "completed", "closed"].includes(status)) {
    notes.push({
      id: "note-2",
      author: technicians[Math.floor(Math.random() * technicians.length)].name,
      content: "Arrived on site. Activity in progress but appears to be authorized. Verifying permits.",
      timestamp: new Date().toISOString(),
    });
  }

  if (["completed", "closed"].includes(status)) {
    notes.push({
      id: "note-3",
      author: technicians[Math.floor(Math.random() * technicians.length)].name,
      content: "Permit verified. Contractor aware of pipeline location. No further action required.",
      timestamp: new Date().toISOString(),
    });
  }

  return notes;
};

const generateActions = (status: WorkOrderStatus, createdDate: Date, techName?: string) => {
  const actions: WorkOrderAction[] = [
    {
      id: "action-1",
      type: "create",
      actor: "System",
      description: "Work order created from detection",
      timestamp: createdDate.toISOString(),
    },
  ];

  if (status !== "open" && techName) {
    actions.push({
      id: "action-2",
      type: "dispatch",
      actor: "Control Room",
      description: `Dispatched to ${techName}`,
      timestamp: new Date(createdDate.getTime() + 15 * 60 * 1000).toISOString(),
    });
  }

  if (["tech_aware", "on_site", "completed", "closed"].includes(status) && techName) {
    actions.push({
      id: "action-3",
      type: "tech_aware",
      actor: techName,
      description: "Acknowledged assignment, en route",
      timestamp: new Date(createdDate.getTime() + 30 * 60 * 1000).toISOString(),
    });
  }

  if (["on_site", "completed", "closed"].includes(status) && techName) {
    actions.push({
      id: "action-4",
      type: "on_site",
      actor: techName,
      description: "Arrived on site",
      timestamp: new Date(createdDate.getTime() + 90 * 60 * 1000).toISOString(),
    });
  }

  if (["completed", "closed"].includes(status) && techName) {
    actions.push({
      id: "action-5",
      type: "complete",
      actor: techName,
      description: "Work completed - no risk identified",
      timestamp: new Date(createdDate.getTime() + 180 * 60 * 1000).toISOString(),
    });
  }

  if (status === "closed") {
    actions.push({
      id: "action-6",
      type: "close",
      actor: "Control Room",
      description: "Work order closed",
      timestamp: new Date(createdDate.getTime() + 240 * 60 * 1000).toISOString(),
    });
  }

  return actions;
};

// Generate 50 work orders
export const workOrdersData: WorkOrder[] = Array.from({ length: 50 }, (_, i) => generateWorkOrder(i + 1));

export const getWorkOrderById = (id: string): WorkOrder | undefined => {
  return workOrdersData.find((wo) => wo.id === id);
};

export const getWorkOrderStats = (): WorkOrderStats => {
  const byPriority = {} as Record<WorkOrderPriority, number>;

  priorities.forEach((priority) => {
    byPriority[priority] = workOrdersData.filter((wo) => wo.priority === priority).length;
  });

  return {
    total: workOrdersData.length,
    open: workOrdersData.filter((wo) => wo.status === "open").length,
    dispatched: workOrdersData.filter((wo) => wo.status === "dispatched").length,
    techAware: workOrdersData.filter((wo) => wo.status === "tech_aware").length,
    onSite: workOrdersData.filter((wo) => wo.status === "on_site").length,
    completed: workOrdersData.filter((wo) => wo.status === "completed").length,
    closed: workOrdersData.filter((wo) => wo.status === "closed").length,
    slaBreached: workOrdersData.filter((wo) => wo.slaBreached).length,
    byPriority,
  };
};

export const getTechnicians = (): Technician[] => technicians;

let nextId = workOrdersData.length + 2; // +2 because generateWorkOrder uses index+1

export const addWorkOrder = (wo: WorkOrder): string => {
  workOrdersData.unshift(wo);
  return wo.id;
};

export const generateWorkOrderId = (): string => {
  const id = `WO-${String(2024001 + nextId).padStart(7, "0")}`;
  nextId++;
  return id;
};
